import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, map, Observable } from 'rxjs';

import { BannerNotice, SummitDisposing } from '@summit/models';

import { MessageService } from 'primeng/api';
import { EventLevel } from '../event-level.enum';
import { EventLog } from '../event-log';
import { EventStatus } from '../event-status.enum';

@Injectable()
export class SummitNotificationService extends SummitDisposing {
  protected notificationsSubject: BehaviorSubject<EventLog[]> = new BehaviorSubject<EventLog[]>([] as EventLog[]);
  public notifications: Observable<EventLog[]> = this.notificationsSubject.asObservable();

  protected bannerBs: BehaviorSubject<BannerNotice | undefined> = new BehaviorSubject<BannerNotice | undefined>(
    undefined
  );
  banner$: Observable<BannerNotice | undefined> = this.bannerBs.asObservable();
  bannerActive$: Observable<boolean> = this.banner$.pipe(map((banner) => banner !== undefined));
  getBannerData(): BannerNotice | undefined {
    return this.bannerBs.value;
  }
  protected messageService: MessageService;

  //private bannerActiveBs: Subject<boolean> = new BehaviorSubject<boolean>(false);

  //bannerDismiss: Subject<boolean> = new Subject<boolean>();
  //onboardingRequired$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  constructor() {
    super();
    this.messageService = inject(MessageService);
  }

  add(newMessage: EventLog) {
    // console.log('New Notification: ', newMessage);
    let severity;
    const currentQueue = this.notificationsSubject.getValue();
    currentQueue.push(newMessage);

    switch (newMessage.eventLevel) {
      case EventLevel.Informational:
        severity = 'info';
        break;
      case EventLevel.Warning:
        severity = 'warn';
        break;
      case EventLevel.Alert:
        severity = 'error';
        break;
      case EventLevel.System:
        severity = 'info';
        break;
      case EventLevel.Unknown:
      default:
        severity = 'warn';
        break;
    }
    this.messageService.add({
      key: 'notificationCustom',
      severity: severity,
      data: newMessage,
      life: 5000
    });

    this.notificationsSubject.next(currentQueue);
  }

  // triggerToastTest() {
  //   this.messageService.add({
  //     key: 'bottom-center-refresh',
  //     severity: 'info',
  //     summary: 'Last Updated:',
  //     sticky: true,
  //     closable: false
  //   });
  // }

  update(eventLog: EventLog) {
    const currentQueue = this.notificationsSubject.getValue();
    const indexOfExistingMessage = currentQueue.findIndex(
      (message) => message.id.toString() === eventLog.id.toString()
    );
    // console.log(`Found @ ${indexOfExistingMessage} id: ${eventLog.id}`, this.notificationsSubject.getValue());
    if (indexOfExistingMessage >= 0) {
      currentQueue[indexOfExistingMessage] = eventLog;
    } else {
      currentQueue.push(eventLog);
    }
    let severity;
    switch (eventLog.eventLevel) {
      case EventLevel.Informational:
        severity = 'info';
        break;
      case EventLevel.Warning:
        severity = 'warn';
        break;
      case EventLevel.Alert:
        severity = 'error';
        break;
      case EventLevel.System:
        severity = 'info';
        break;
      case EventLevel.Unknown:
      default:
        severity = 'warn';
        break;
    }
    this.messageService.add({
      key: 'notificationCustom',
      severity: severity,
      data: eventLog,
      life: 5000
    });
    // console.log(`Adding msg to Notification Queue`, eventLog, currentQueue);
    this.notificationsSubject.next(currentQueue);
  }
  openOfflineToast() {
    this.messageService.add({
      key: 'notification',
      severity: 'warn',
      summary: 'Warning',
      detail: 'You are not currently connected to the internet',
      life: 3000
    });
  }
  triggerToast(
    message: string,
    duration: number,
    severity: 'success' | 'warn' | 'info' | 'error' = 'success',
    eventStatus: EventStatus = EventStatus.Unknown,
    actionText: '' | 'Go To' = '',
    stringURL?: string,
    update?: boolean
  ) {
    let eventLevel;
    switch (severity) {
      case 'info':
        eventLevel = EventLevel.Informational;
        break;
      case 'warn':
        eventLevel = EventLevel.Warning;
        break;
      case 'error':
        eventLevel = EventLevel.Alert;
        break;
      case null:
        severity = 'info';
        eventLevel = EventLevel.Informational;
        break;
      case 'success':
        eventLevel = EventLevel.Informational;
        break;
      default:
        eventLevel = EventLevel.Unknown;
        break;
    }

    const eventLog = EventLog.NewEventFrom(eventLevel, message);

    if (actionText?.length != 0) eventLog.urlLabel = actionText;
    if (actionText == 'Go To' && stringURL) {
      eventLog.urlLink = stringURL;
    }
    eventLog.status = eventStatus;

    if (update) {
      //console.log('Update Toast', eventLog);
      eventLog.updateLabel = 'Reload';
      this.triggerUpdateToast(eventLog, duration, severity);
    } else {
      //console.log('Toast', eventLog);
      this.triggerToastCustom(eventLog, duration, severity);
    }
  }
  triggerToastCustom(message: EventLog, duration: number, severity: string) {
    //console.log('Custom Trigger Toast', message, duration, severity);
    this.messageService.add({
      key: 'notificationCustom',
      severity: severity,
      data: message,
      life: duration * 1000
    });
    //console.log('message', this.messageService);
  }
  triggerUpdateToast(message: EventLog, duration: number, severity: string) {
    this.messageService.add({
      key: 'notificationUpdate',
      severity: severity,
      data: message,
      sticky: true
    });
  }

  clear() {
    this.bannerBs.next(undefined);
    //const currentQueue = this.notificationsSubject.getValue();
    //currentQueue.length = 0;
    //this.notificationsSubject.next(currentQueue);
    //this.messageService.clear('notificationCustom');
  }
  saved() {
    this.messageService.add({
      key: 'notificationOneLine',
      severity: 'success',
      summary: 'Saved',
      life: 3000
    });
  }
  /*
   * This method is used to get current banner to display
   */
  injectBanner(bannerNotice?: BannerNotice) {
    this.bannerBs.next(bannerNotice);
    // //console.log('Banner Notice:', bannerNotice);
    // if (bannerNotice) {
    //   this._bannerData = bannerNotice;
    //   this.onboardingRequired$.next(true);
    //   this.bannerActiveBs.next(true);
    // } else {
    //   this.clearBanner();
    // }
  }
  clearBanner() {
    this.bannerBs.next(undefined);
    //console.log('Clearing Banner');
    //this._bannerData = undefined;
    //this.bannerActiveBs.next(false);
  }

  showProgressSpinner(_show: boolean, _text?: string) {
    console.error('showProgressSpinner needs to be implemented in child class');
  }
}
